import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { PreviewImage, SkeletonContainer, VideoComponent, VideoContainer } from './Video.styles';

const Video = forwardRef(
  (
    {
      src,
      posterSrc,
      posterAlt = '',
      className,
      onClick,
      onLoadedData,
      onLoadedMetadata,
      controls = false,
      disabled,
      autoPlay = false,
      muted = true,
      onTimeUpdate,
      playWhenVisible = true,
      primeFrameOnLoad = false,
      style,
      ...rest
    },
    ref,
  ) => {
    const videoRef = useRef(null);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [previewLoaded, setPreviewLoaded] = useState(false);

    useEffect(() => {
      setVideoLoaded(false);
    }, [src]);

    useEffect(() => {
      setPreviewLoaded(false);
    }, [posterSrc]);

    const hasPreviewImage = Boolean(posterSrc) && previewLoaded;
    const showLoader = !videoLoaded && !hasPreviewImage;
    const showPreview = hasPreviewImage && !videoLoaded;

    useImperativeHandle(ref, () => ({
      play: () => videoRef.current?.play(),
      pause: () => videoRef.current?.pause(),
      mute: () => {
        if (videoRef.current) videoRef.current.muted = true;
      },
      unmute: () => {
        if (videoRef.current) videoRef.current.muted = false;
      },
      toggleMute: () => {
        if (videoRef.current) videoRef.current.muted = !videoRef.current.muted;
      },
      get isMuted() {
        return videoRef.current?.muted;
      },
      get paused() {
        return videoRef.current?.paused;
      },
      get currentTime() {
        return videoRef.current?.currentTime;
      },
      get duration() {
        return videoRef.current?.duration;
      },
      set currentTime(val) {
        if (videoRef.current) videoRef.current.currentTime = val;
      },
    }));

    useEffect(() => {
      if (!playWhenVisible) {
        return undefined;
      }

      const video = videoRef.current;
      if (!video) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        },
        { threshold: 0.5 },
      );

      observer.observe(video);

      return () => observer.disconnect();
    }, [playWhenVisible]);

    const handleLoadedMetadata = async e => {
      const video = e.currentTarget;
      const shouldPrimeFrame = primeFrameOnLoad && !autoPlay && !playWhenVisible;

      onLoadedMetadata?.(e);

      if (!autoPlay && !playWhenVisible && !shouldPrimeFrame) {
        return;
      }

      try {
        await video.play();
        if (!autoPlay) {
          video.pause();
        }
      } catch (error) {
        console.log(error);
      }
    };

    const handleLoadedData = e => {
      setVideoLoaded(true);
      onLoadedData?.(e);
    };

    return (
      <VideoContainer
        className={className}
        onClick={!disabled ? onClick : undefined}
        $cursorPointer={!!onClick && !disabled}>
        {showLoader ? <SkeletonContainer $animationTime={6} /> : null}
        {posterSrc ? (
          <PreviewImage
            src={posterSrc}
            alt={posterAlt}
            onLoad={() => setPreviewLoaded(true)}
            $visible={showPreview}
          />
        ) : null}
        <VideoComponent
          ref={videoRef}
          controls={controls}
          muted={muted}
          loop
          playsInline
          poster={hasPreviewImage ? undefined : posterSrc}
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={onTimeUpdate}
          onLoadedData={handleLoadedData}
          {...rest}
          style={{ ...style, opacity: videoLoaded ? 1 : 0 }}>
          <source src={src} type="video/mp4" />
        </VideoComponent>
      </VideoContainer>
    );
  },
);

export default Video;
